import {
  Injectable,
  inject,
} from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {
  from,
  Observable,
} from 'rxjs';
import {mergeMap} from 'rxjs/operators';
import {ApiUrlService} from '../../api/core/api-url/api-url.service';
import {OidcAuthService} from '../oidc-auth/oidc-auth.service';
import {IframeService} from '../../iframe/iframe.service';
import {IframeAuthService} from '../iframe-auth/iframe-auth.service';
import {SharedLinkService} from '../../sharing/shared-link.service';
import {Constants} from '../../../../common/constants/constants';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {

  private apiUrlService = inject(ApiUrlService);

  private iframeService = inject(IframeService);

  private iframeAuthService = inject(IframeAuthService);

  private sharedLinkService = inject(SharedLinkService);

  private oidcAuthService = inject(OidcAuthService);

  /**
   * Method: Add custom header for requests
   * @param request
   * @param typeToken
   * @param currentToken
   * @private
   */
  private static addToken(request: HttpRequest<any>, typeToken: string, currentToken: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `${typeToken} ${currentToken}`,
      },
    });
  }

  /**
   * Method: Add custom header X-REFERER-URI for requests
   * @param request
   * @private
   */
  private static addXRefererUri(request: HttpRequest<any>): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        'X-REFERER-URI': window.location.href,
      },
    });
  }

  /**
   * Method: Intercept requests
   * @param request
   * @param next
   * @private
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.iframeService.isIframePage()) {
      return next.handle(this.iframeRequest(request));
    }
    if (this.sharedLinkService.isSharedLinkPage()) {
      return next.handle(this.sharedLinkRequest(request));
    }
    if (request.url.includes(this.apiUrlService.s3BucketDomainUrl)) {
      return next.handle(request);
    }
    return from(
      this.oidcAuthService.getUserObservable().pipe(mergeMap(user => next.handle(this.basicRequest(request, user)))),
    );
  }

  /**
   * Method: Get basic request with additional headers
   * @param request
   * @param user
   */
  basicRequest(request: HttpRequest<any>, user: any): HttpRequest<any> {
    let requestValue = request;
    const isApiUrl = this.checkRequestUrls(requestValue);
    const currentToken = user?.access_token;
    const typeToken = user?.token_type;
    const isLoggedIn = !!currentToken && !!typeToken;
    if (isLoggedIn && isApiUrl) {
      requestValue = JwtInterceptor.addToken(requestValue, typeToken, currentToken);
    }
    requestValue = requestValue.clone({withCredentials: true});
    return requestValue;
  }

  /**
   * Method: Get iframe request with additional headers
   * @param request
   */
  iframeRequest(request: HttpRequest<any>): HttpRequest<any> {
    let requestValue = request;
    const isApiUrl = this.checkRequestUrls(requestValue);
    const iframeToken = this.iframeAuthService.getIframeToken();
    const tokenType = Constants.CONST_TOKEN_TYPE;
    if (iframeToken && isApiUrl) {
      // add auth header with jwt if user is logged in and request is to api url
      requestValue = JwtInterceptor.addToken(requestValue, tokenType, iframeToken);
    }
    requestValue = requestValue.clone({withCredentials: true});
    return requestValue;
  }

  /**
   * Method: Get shared link request with additional headers
   * @param request
   */
  sharedLinkRequest(request: HttpRequest<any>): HttpRequest<any> {
    let requestValue = request;
    const isApiUrl = this.checkRequestUrls(requestValue);
    const {token} = this.sharedLinkService;
    const tokenType = Constants.CONST_TOKEN_TYPE;
    if (token && isApiUrl) {
      // add auth header with jwt if user is logged in and request is to api url
      requestValue = JwtInterceptor.addToken(requestValue, tokenType, token);
      requestValue = JwtInterceptor.addXRefererUri(requestValue);
    }
    requestValue = requestValue.clone({withCredentials: true});
    return requestValue;
  }

  /**
   * Method: Check request urls
   * @param request
   * @private
   */
  private checkRequestUrls(request: HttpRequest<any>): boolean {
    return (
      request.url.startsWith(this.apiUrlService.localhostApiUrl)
      || request.url.startsWith(this.apiUrlService.baseAuthApiUrl)
      || request.url.startsWith(this.apiUrlService.baseGraphQlApiUrl)
      || request.url.startsWith(this.apiUrlService.gatewayApiUrl)
    );
  }

}
